import { useState, useRef, useEffect } from 'react'
import { Calendar, ChevronDown, Check, X, Loader2 } from 'lucide-react'
import type { AvailableCalendar } from '@/hooks/useAssistantCalendarSettings'

interface CalendarSelectorProps {
  calendars: AvailableCalendar[]
  value: string | null
  onChange: (calendarId: string | null) => void
  placeholder?: string
  disabled?: boolean
  loading?: boolean
}

interface MultiCalendarSelectorProps {
  calendars: AvailableCalendar[]
  value: string[]
  onChange: (calendarIds: string[]) => void
  placeholder?: string
  disabled?: boolean
  loading?: boolean
}

export function CalendarSelector({
  calendars,
  value,
  onChange,
  placeholder = 'Selecione uma agenda',
  disabled,
  loading
}: CalendarSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedCalendar = calendars.find(c => c.id === value)

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-left hover:border-zinc-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {loading ? (
            <Loader2 className="w-4 h-4 text-zinc-500 animate-spin flex-shrink-0" />
          ) : (
            <Calendar className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          )}
          {selectedCalendar ? (
            <div className="flex items-center gap-2 min-w-0">
              {selectedCalendar.backgroundColor && (
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: selectedCalendar.backgroundColor }}
                />
              )}
              <span className="text-sm text-white truncate">
                {selectedCalendar.name}
                {selectedCalendar.primary && (
                  <span className="text-zinc-500 ml-1">(Principal)</span>
                )}
              </span>
            </div>
          ) : (
            <span className="text-sm text-zinc-500">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && calendars.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg bg-zinc-800 border border-zinc-700 shadow-xl">
          {calendars.map(cal => (
            <button
              key={cal.id}
              type="button"
              onClick={() => {
                onChange(cal.id)
                setIsOpen(false)
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-zinc-700/50 transition-colors ${
                value === cal.id ? 'bg-zinc-700/30' : ''
              }`}
            >
              {cal.backgroundColor && (
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cal.backgroundColor }}
                />
              )}
              <span className="text-sm text-white flex-1 truncate">
                {cal.name}
                {cal.primary && (
                  <span className="text-zinc-500 ml-1">(Principal)</span>
                )}
              </span>
              {value === cal.id && (
                <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function MultiCalendarSelector({
  calendars,
  value,
  onChange,
  placeholder = 'Selecione as agendas',
  disabled,
  loading
}: MultiCalendarSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedCalendars = calendars.filter(c => value.includes(c.id))

  const toggleCalendar = (calendarId: string) => {
    if (value.includes(calendarId)) {
      onChange(value.filter(id => id !== calendarId))
    } else {
      onChange([...value, calendarId])
    }
  }

  const removeCalendar = (calendarId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(value.filter(id => id !== calendarId))
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-left hover:border-zinc-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[42px]"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
          {loading ? (
            <Loader2 className="w-4 h-4 text-zinc-500 animate-spin flex-shrink-0" />
          ) : (
            <Calendar className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          )}
          {selectedCalendars.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedCalendars.map(cal => (
                <span
                  key={cal.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-zinc-700 text-white"
                >
                  {cal.backgroundColor && (
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: cal.backgroundColor }}
                    />
                  )}
                  <span className="truncate max-w-[100px]">{cal.name}</span>
                  <button
                    type="button"
                    onClick={(e) => removeCalendar(cal.id, e)}
                    className="hover:text-red-400 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <span className="text-sm text-zinc-500">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && calendars.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg bg-zinc-800 border border-zinc-700 shadow-xl">
          {calendars.map(cal => (
            <button
              key={cal.id}
              type="button"
              onClick={() => toggleCalendar(cal.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-zinc-700/50 transition-colors ${
                value.includes(cal.id) ? 'bg-zinc-700/30' : ''
              }`}
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                value.includes(cal.id)
                  ? 'bg-emerald-500 border-emerald-500'
                  : 'border-zinc-600'
              }`}>
                {value.includes(cal.id) && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              {cal.backgroundColor && (
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cal.backgroundColor }}
                />
              )}
              <span className="text-sm text-white flex-1 truncate">
                {cal.name}
                {cal.primary && (
                  <span className="text-zinc-500 ml-1">(Principal)</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
